import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Loader, Nav, Panel } from 'rsuite';
import UserForm from './user-form';

import { User } from '@/models/user.model';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { getUser } from '@/services/user.service';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import ActivityInfoList from '@/pages/activity-info/list/activity-info-list';
import UserAchievementList from './user-achievement-list';
import UserActions from './user-actions';
import UserSettingForm from './user-setting-form';
import PageHeader from '@/components/PageHeader';
import AdvanceShareList from '@/pages/advance-share/list/advance-share-list';
import AdvanceShareTemplateList from '@/pages/advance-share/template/list/advance-share-template-list';
import PointTransactionList from '@/pages/point-transaction/list/point-transaction-list';
import RunningClubMemberList from '@/pages/running-club/running-club-member/running-club-member-list';

const CustomNav = ({ active, onSelect, newMode, ...props }) => {
  return (
    <Nav {...props} activeKey={active} onSelect={onSelect} >
      <Nav.Item eventKey="user-detail">User Detail</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="user-activities">Activities</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="user-achievements">Achievements</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="user-points">Points</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="user-advance-share-requests">Advance Share Requests</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="user-advance-share-templates">Advance Share Templates</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="user-point-transactions">Point Transactions</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="user-running-clubs">Clubs</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="user-settings">Settings</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="user-actions">Actions</Nav.Item>
    </Nav>
  );
};

const Page = () => {

  const requestedTab = useLocation()?.state?.tab;

  const [activeTab, setActiveTab] = useState('user-detail');
  const [formObject, setFormObject] = useState<User|undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;
  
  useEffect(() => {
    if (pageState == PageState.Edit) {
      fetchData();
    } else {
      setFormObject(undefined);
    }
    setActiveTab(pre => requestedTab ?? pre);
  }, [id]);
  
  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<User> = await getUser(id);
        if (result.success) {
          setFormObject(result.result);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching user: ${errorModel?.errorMessage}`);
        nav('/users');
      }
      finally {
        setLoading(false);
      }
    }
  };

  return (
    <Panel
     header = {
     <PageHeader title = {pageState == PageState.Edit ? `Edit User: ${formObject?.firstName}  ${formObject?.lastName}` : 'New User'}>
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item href='/users'>Users</Breadcrumb.Item>
        <Breadcrumb.Item active>{pageState == PageState.Edit ? 'Edit' : 'New'}</Breadcrumb.Item>
      </Breadcrumb>
     </PageHeader>
    }>
      {
        loading ? <Loader center content="loading ..." /> :
        <>
          <CustomNav appearance="subtle" active={activeTab} onSelect={key => setActiveTab(key)} newMode={pageState == PageState.New} />
          {activeTab == 'user-detail' && <UserForm formObject={formObject} pageState={pageState}/>}
          {activeTab == 'user-activities' && id && <ActivityInfoList userId={id}/>}
          {activeTab == 'user-achievements' && id && <UserAchievementList userId={id}/>}
          {activeTab == 'user-points' && id && <PointTransactionList userId={id}/>}
          {activeTab == 'user-advance-share-requests' && <AdvanceShareList userId={formObject?.id} />}
          {activeTab == 'user-advance-share-templates' && <AdvanceShareTemplateList userId={formObject?.id} />}
          {activeTab == 'user-point-transactions' && <PointTransactionList userId={formObject?.id} />}
          {activeTab == 'user-running-clubs' && <RunningClubMemberList userId={formObject?.id} />}
          {activeTab == 'user-settings' && <UserSettingForm user={formObject} />}
          {activeTab == 'user-actions' && id && <UserActions formObject={formObject} />}
        </>
      }
    </Panel>
  );
};

export default Page;
